import { useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import styles from './GoogleMap.module.scss';
import SearchBar from '../../DesignSystem/SearchBar';
import { ReactComponent as LocatorIcon } from '../../Icons/Locator.svg';

const defaultCenter = {
  lat: 28.7041,
  lng: 77.1025,
};

const mapStyle = [
  {
    featureType: 'all',
    elementType: 'labels',
    stylers: [{ visibility: 'on' }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#e0f2f1' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#a8e6a3' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
];

function processGeocodeResponse(result) {
  if (!result) return null;

  const components = result.address_components || [];
  console.log('components', components);
  const find = (type) =>
    components.find((c) => c.types.includes(type))?.long_name || '';

  console.log('formatted_address', result.formatted_address);

  const customAddress = [
    find('premise'),
    find('sublocality_level_2'),
    find('street_number'),
    find('route'),
    find('sublocality_level_1'),
    find('locality'),
    find('administrative_area_level_1'),
    find('postal_code'),
    find('country'),
  ]
    .filter(Boolean)
    .join(', ');

  return {
    fullAddress: customAddress,
    city: find('locality') || find('sublocality_level_1'),
    locality: find('locality') || find('sublocality_level_1'),
    pincode: find('postal_code'),
    lat: result.geometry.location.lat(),
    long: result.geometry.location.lng(),
  };
}

export default function MyMap({ setText, onConfirm, initialCoords }) {
  const [markerPos, setMarkerPos] = useState(defaultCenter);
  const [structuredAddress, setStructuredAddress] = useState(null);
  const mapRef = useRef(null);

  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries: ['places'],
  });

  /* Initial marker */
  useEffect(() => {
    if (initialCoords) {
      setMarkerPos(initialCoords);
    }
  }, [initialCoords]);

  /* Autocomplete */
  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        fields: ['geometry', 'address_components', 'formatted_address'],
        componentRestrictions: { country: 'in' },
      }
    );

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (!place?.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      setMarkerPos({ lat, lng });
      fetchAddress(lat, lng);

      mapRef.current?.panTo({ lat, lng });
      mapRef.current?.setZoom(17);
    });
  }, [isLoaded]);

  const fetchAddress = (lat, lng) => {
    console.log('fetch', lat, lng);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const data = processGeocodeResponse(results[0]);
        console.log('data', data);
        setStructuredAddress(data);
        setText?.(data);
      }
    });
  };

  const onLoad = (map) => {
    mapRef.current = map;
    map.setOptions({ styles: mapStyle, disableDefaultUI: true });
    map.setZoom(16);

    fetchAddress(markerPos.lat, markerPos.lng);
  };

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        setMarkerPos({ lat, lng });
        fetchAddress(lat, lng);

        mapRef.current?.panTo({ lat, lng });
        mapRef.current?.setZoom(17);
      },
      () => alert('Location permission denied'),
      { enableHighAccuracy: true }
    );
  };

  if (!isLoaded) return <p>Loading map...</p>;

  return (
    <div className={styles.mapWrapper}>
      {/* Search */}
      <div className={styles.searchWrapper}>
        <SearchBar
          placeholder="Search location"
          inputRef={inputRef}
          iconColor="#bfc1c6ff"
          placeholderColor="#bfc1c6ff"
        />
      </div>

      {/* Locator */}
      <button className={styles.locatorBtn} onClick={getCurrentLocation}>
        <LocatorIcon />
      </button>

      {/* Map Area */}
      <div className={styles.mapArea}>
        <GoogleMap
          mapContainerClassName={styles.mapContainer}
          center={markerPos}
          zoom={15}
          onLoad={onLoad}
          onClick={(e) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            setMarkerPos({ lat, lng });
            fetchAddress(lat, lng);
          }}
        >
          <Marker position={markerPos} />
        </GoogleMap>
      </div>

      {/* Bottom Bar (ALWAYS VISIBLE) */}
      {structuredAddress && (
        <div className={styles.bottomBar}>
          <div className={styles.content}>
            <LocatorIcon />
            <div>
              <p className={styles.address}>{structuredAddress.fullAddress}</p>
              <p className={styles.city}>{structuredAddress.city}</p>
            </div>
          </div>

          <button
            className={styles.confirmButton}
            onClick={() => onConfirm(structuredAddress)}
          >
            Confirm Address
          </button>
        </div>
      )}
    </div>
  );
}
